import React, { useState, useMemo } from 'react';
import { TeamDetailed } from '@/types';
import { isAnyWearableConnected } from '@/config/wearableProviders';
import { useModals } from '@/contexts/ModalContext';

/**
 * Props for the TeamsTab component.
 */
interface TeamsTabProps {
    /** List of teams with detailed membership information */
    teams: TeamDetailed[];
    /** Whether dark mode is enabled */
    isDark: boolean;
    /** Currently copied message (for clipboard feedback) */
    copiedMessage: string;
    /** Function to copy text to clipboard */
    copyToClipboard: (text: string) => void;
    /** Function to delete a team */
    onDeleteTeam: (teamId: number) => Promise<void>;
    /** Function to send email digest to selected coaches */
    onSendEmailDigest: (coachIds: number[]) => Promise<{ success: boolean; message: string }>;
}

/**
 * Represents a coach with their user info and team memberships.
 */
interface CoachInfo {
    userId: number;
    email: string;
    fullName: string;
    teamNames: string[];
}

/**
 * TeamsTab component for the admin dashboard.
 * Displays team management interface with coach email digest functionality.
 */
const TeamsTab: React.FC<TeamsTabProps> = ({
    teams,
    isDark,
    copiedMessage,
    copyToClipboard,
    onDeleteTeam,
    onSendEmailDigest
}) => {
    const { openCreateTeamModal } = useModals();
    // State for team deletion
    const [deletingTeamId, setDeletingTeamId] = useState<number | null>(null);
    
    // State for coach selection and email sending
    const [selectedCoachIds, setSelectedCoachIds] = useState<Set<number>>(new Set());
    const [sendingDigest, setSendingDigest] = useState<boolean>(false);
    const [digestMessage, setDigestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    
    /**
     * Get all unique coaches across all teams.
     * Each coach appears once with all their team names listed.
     */
    const allCoaches = useMemo((): CoachInfo[] => {
        const coachMap = new Map<number, CoachInfo>();
        
        teams.forEach(team => {
            team.memberships.forEach(membership => {
                // Check if this member is a coach (membership role or global role)
                const roles = String(membership.role).split(',').map(r => r.trim());
                const isCoach = roles.includes('coach') || membership.user_global_role === 'coach';
                
                if (isCoach) {
                    const existing = coachMap.get(membership.user_id);
                    if (existing) {
                        // Add team name if not already present
                        if (!existing.teamNames.includes(team.name)) {
                            existing.teamNames.push(team.name);
                        }
                    } else {
                        coachMap.set(membership.user_id, {
                            userId: membership.user_id,
                            email: membership.email,
                            fullName: membership.full_name || membership.username,
                            teamNames: [team.name]
                        });
                    }
                }
            });
        });
        
        return Array.from(coachMap.values()).sort((a, b) => 
            a.fullName.localeCompare(b.fullName)
        );
    }, [teams]);
    
    /**
     * Handle team deletion with confirmation.
     */
    const handleDelete = async (team: TeamDetailed) => {
        if (!confirm(`Are you sure you want to delete "${team.name}"? This will remove all ${team.memberships.length} members from the team.`)) {
            return;
        }
        
        try {
            setDeletingTeamId(team.id);
            await onDeleteTeam(team.id);
        } finally {
            setDeletingTeamId(null);
        }
    };
    
    /**
     * Toggle coach selection.
     */
    const toggleCoachSelection = (coachId: number) => {
        setSelectedCoachIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(coachId)) {
                newSet.delete(coachId);
            } else {
                newSet.add(coachId);
            }
            return newSet;
        });
    };
    
    /**
     * Select or deselect all coaches.
     */
    const toggleSelectAll = () => {
        if (selectedCoachIds.size === allCoaches.length) {
            setSelectedCoachIds(new Set());
        } else {
            setSelectedCoachIds(new Set(allCoaches.map(c => c.userId)));
        }
    };
    
    /**
     * Send email digest to selected coaches.
     */
    const handleSendDigest = async () => {
        if (selectedCoachIds.size === 0) {
            setDigestMessage({ type: 'error', text: 'Please select at least one coach' });
            return;
        }
        
        setSendingDigest(true);
        setDigestMessage(null);
        
        try {
            const result = await onSendEmailDigest(Array.from(selectedCoachIds));
            setDigestMessage({ 
                type: result.success ? 'success' : 'error', 
                text: result.message 
            });
            
            if (result.success) {
                // Clear selection after successful send
                setSelectedCoachIds(new Set());
            }
        } catch (error) {
            setDigestMessage({ 
                type: 'error', 
                text: 'Failed to send email digest' 
            });
        } finally {
            setSendingDigest(false);
            // Clear message after 5 seconds
            setTimeout(() => setDigestMessage(null), 5000);
        }
    };
    
    return (
    <div className="space-y-6">
        {/* Header with Create Team button */}
        <div className="cgm-card p-6">
            <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold cgm-text-primary">Team Management</h3>
                <button
                    onClick={openCreateTeamModal}
                    className="cgm-button px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
                >
                    Create Team
                </button>
            </div>
        </div>

        {/* Email Digest Section - Only show if there are coaches */}
        {allCoaches.length > 0 && (
            <div className="cgm-card p-6">
                <div className="flex flex-col space-y-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h3 className="text-lg font-bold cgm-text-primary">Send Email Digest</h3>
                            <p className="text-sm cgm-text-secondary">
                                Select coaches to send their daily digest email immediately
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <span className="text-sm cgm-text-secondary">
                                {selectedCoachIds.size} of {allCoaches.length} selected
                            </span>
                            <button
                                onClick={handleSendDigest}
                                disabled={selectedCoachIds.size === 0 || sendingDigest}
                                className="cgm-button px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {sendingDigest ? 'Sending...' : 'Send Email Digest'}
                            </button>
                        </div>
                    </div>
                    
                    {/* Status Message */}
                    {digestMessage && (
                        <div className={`p-3 rounded-lg text-sm font-medium ${digestMessage.type === 'success' ? 'cgm-alert-success' : 'cgm-alert-error'}`}>
                            {digestMessage.text}
                        </div>
                    )}
                    
                    {/* Coach Selection List */}
                    <div className="border border-(--card-border) rounded-xl overflow-hidden">
                        {/* Select All Header */}
                        <div className="cgm-list-item flex items-center px-4 py-3 border-b border-(--card-border)">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedCoachIds.size === allCoaches.length && allCoaches.length > 0}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                                <span className="ml-3 text-sm font-medium cgm-text-primary">
                                    Select All Coaches
                                </span>
                            </label>
                        </div>
                        
                        {/* Coach List */}
                        <div className="max-h-64 overflow-y-auto">
                            {allCoaches.map((coach, index) => (
                                <label
                                    key={coach.userId}
                                    className={`cgm-list-item flex items-center px-4 py-3 cursor-pointer transition-colors ${index !== allCoaches.length - 1 ? 'border-b border-(--card-border)' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedCoachIds.has(coach.userId)}
                                        onChange={() => toggleCoachSelection(coach.userId)}
                                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                    />
                                    <div className="ml-3 flex-1">
                                        <div className="text-sm font-medium cgm-text-primary">
                                            {coach.fullName}
                                        </div>
                                        <div className="text-xs cgm-text-secondary">
                                            {coach.email}
                                        </div>
                                    </div>
                                    <div className="text-xs cgm-text-secondary">
                                        {coach.teamNames.length === 1 
                                            ? coach.teamNames[0] 
                                            : `${coach.teamNames.length} teams`}
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Team Cards */}
        {teams.map((team) => (
            <div key={team.id} className="cgm-card overflow-hidden">
                <div className="p-6 border-b border-(--card-border)">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <h3 className="text-xl font-bold cgm-text-primary">
                                {team.name}
                            </h3>
                            <p className="text-sm cgm-text-secondary">
                                {team.organization.name} • {team.memberships.length} members
                            </p>
                        </div>

                        {/* Delete Button */}
                        <div className="mr-4">
                            <button
                                onClick={() => handleDelete(team)}
                                disabled={deletingTeamId === team.id}
                                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-red-600 hover:bg-red-700 cgm-text-tertiary transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-red-600"
                            >
                                {deletingTeamId === team.id ? 'Deleting...' : 'Delete Team'}
                            </button>
                        </div>

                        {/* Invitation Codes */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                                <div className="text-right">
                                    <div className="text-xs font-medium cgm-text-primary mb-1">Coach Code</div>
                                    <div className="flex items-center space-x-2">
                                        <code className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-3 py-1 rounded-lg font-code text-sm">
                                            {team.coach_code}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(team.coach_code)}
                                            className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${copiedMessage === team.coach_code
                                                ? 'bg-green-500 cgm-text-tertiary'
                                                : 'bg-blue-500 cgm-text-tertiary hover:bg-blue-600'
                                                }`}
                                            title="Copy coach code"
                                        >
                                            {copiedMessage === team.coach_code ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2">
                                <div className="text-right">
                                    <div className="text-xs font-medium cgm-text-primary mb-1">Athlete Code</div>
                                    <div className="flex items-center space-x-2">
                                        <code className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-3 py-1 rounded-lg font-code text-sm">
                                            {team.athlete_code}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(team.athlete_code)}
                                            className={`px-3 py-1 text-xs rounded-lg font-medium transition-colors ${copiedMessage === team.athlete_code
                                                ? 'bg-green-500 cgm-text-tertiary'
                                                : 'bg-green-500 cgm-text-tertiary hover:bg-green-600'
                                                }`}
                                            title="Copy athlete code"
                                        >
                                            {copiedMessage === team.athlete_code ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {team.description && (
                        <p className="mt-2 cgm-text-secondary">{team.description}</p>
                    )}
                </div>

                <div className="cgm-table-wrap">
                    <table className="cgm-table cgm-table-striped">
                        <thead>
                            <tr>
                                <th>Member</th>
                                <th>Role</th>
                                <th>Joined</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {team.memberships.map((membership) => (
                                <tr key={membership.user_id}>
                                    <td>
                                        <div>
                                            <div className="text-sm font-medium cgm-text-primary">
                                                {membership.full_name || membership.username}
                                            </div>
                                            <div className="text-sm cgm-text-secondary">{membership.email}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-wrap gap-2">
                                            {/* Render one badge per membership role (supports multi-role strings like "coach,athlete") */}
                                            {String(membership.role)
                                                .split(',')
                                                .map(r => r.trim())
                                                .filter(Boolean)
                                                .map((r) => (
                                                    <span key={r} className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${r === 'coach'
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                    }`}>
                                                        {r}
                                                    </span>
                                                ))}
                                            {/* Global role badge if different and not already included */}
                                            {membership.user_global_role && !String(membership.role).split(',').map(r => r.trim()).includes(membership.user_global_role) && (
                                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${membership.user_global_role === 'admin'
                                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                    : membership.user_global_role === 'coach'
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                }`}>
                                                    {membership.user_global_role}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-sm cgm-text-primary">
                                        {new Date(membership.joined_at).toLocaleDateString()}
                                    </td>
                                    <td className={`px-6 py-4`}>
                                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${isAnyWearableConnected(membership)
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                            }`}>
                                            <span className={`cgm-status-dot ${isAnyWearableConnected(membership) ? 'cgm-status-connected' : 'cgm-status-warning'}`} />
                                            <span>{isAnyWearableConnected(membership) ? 'Connected' : 'Not Connected'}</span>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        ))}
    </div>
    );
};

export default TeamsTab;
